import { Injectable, inject } from '@angular/core';
import { MatSnackBar, MatSnackBarRef, TextOnlySnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { DialogService } from '../dialog.service';
import { LocalStorageService } from '../privacy/storage/local-storage.service';
import { JobPostingResponse } from '../openapi/model/jobPostingResponse';

@Injectable({
  providedIn: 'root',
})
export class ApplicationService {
  private readonly router: Router = inject(Router);
  private readonly snackBar: MatSnackBar = inject(MatSnackBar);
  private readonly dialogService: DialogService = inject(DialogService);
  private readonly localStorageService: LocalStorageService = inject(LocalStorageService);
  private jobAlreadyAppliedSnackBar!: MatSnackBarRef<TextOnlySnackBar>;
  private _activeJobPosting: JobPostingResponse | null = null;
  private isSnackBarOpen = false;

  public get activeJobPosting(): JobPostingResponse | null {
    return this._activeJobPosting;
  }

  public set activeJobPosting(jobPosting: JobPostingResponse) {
    if (!jobPosting) {
      return;
    }
    this._activeJobPosting = jobPosting;
    this.localStorageService.ActiveJobPostingNumber = jobPosting.jobPostingNumber;
  }

  public get hasActiveJobPosting(): boolean {
    return !!this.activeJobPosting;
  }

  public get activeJobPostingNumber(): string | null {
    return this.localStorageService.ActiveJobPostingNumber ?? null;
  }

  public clearActiveJobPosting() {
    this._activeJobPosting = null;
    this.localStorageService.ActiveJobPostingNumber = null;
    this.dismissJobApplicationSnackBar();
  }

  public openJobApplicationSnackBar(displayText: string, undoButtonText: string, displayAlert: boolean) {
    if (!this.isSnackBarOpen) {
      this.jobAlreadyAppliedSnackBar = this.snackBar.open(displayText, undoButtonText, { duration: undefined });
      this.isSnackBarOpen = true;
      this.jobAlreadyAppliedSnackBar.onAction().subscribe(() => {
        if (displayAlert) {
          this.dialogService
            .openConfirmationDialog(
              'Cancel Application',
              'Are you sure you wish to cancel this application? You can complete this application at a later time by going to Your Applications.',
              'Yes',
              'No'
            )
            .subscribe((result) => {
              if (result) {
                this.cancelApplication();
                this.dismissJobApplicationSnackBar();
              } else {
                this.openJobApplicationSnackBar(displayText, undoButtonText, displayAlert);
              }
            });
        } else {
          this.cancelApplication();
          this.dismissJobApplicationSnackBar();
        }
      });

      this.jobAlreadyAppliedSnackBar.afterDismissed().subscribe(() => {
        this.isSnackBarOpen = false;
      });
    }
  }

  public dismissJobApplicationSnackBar() {
    if (this.jobAlreadyAppliedSnackBar) {
      this.jobAlreadyAppliedSnackBar.dismiss();
      this.isSnackBarOpen = false;
    }
  }

  private cancelApplication() {
    this.router.navigate(['/applications']);
    this.clearActiveJobPosting();
  }
}
